import {Component, Input, OnInit} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import {
  ConfigurationState,
  GeneralOptionsGroupName,
  InventoryService,
  PostingDataResponseModel,
  VehicleOption,
  EditPostingState,
  UpdatePosting,
  EditPostingFormNames
} from 'remarketing-angular-library';
import {
  find as _find,
  set as _set,
  cloneDeep as _cloneDeep
} from 'lodash';
import { ExtraOption } from 'remarketing-angular-library/models/posting-data-response.model';
import { OptionsAndAccessoriesFormsService } from '../../services/options-and-accessories-forms.service';
import { SubGroupName, SubGroupResponseName } from '../../data/option-sub-group-mapping';
import { takeUntil } from 'rxjs/operators';
import { PostingFormDirective } from '../posting-form/posting-form.directive';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  OptionsAndAccessoriesFormComponent
} from '../options-and-accessories-form/options-and-accessories-form.component';


@Component({
    selector: 'app-mechanical-form',
    templateUrl: './mechanical-form.component.html',
    styleUrls: ['./mechanical-form.component.scss'],
    imports: [TranslocoDirective, ReactiveFormsModule, OptionsAndAccessoriesFormComponent]
})
export class MechanicalFormComponent extends PostingFormDirective implements OnInit {
  mechanicalForm: UntypedFormGroup;
  postingData: PostingDataResponseModel;
  originalPostingData: PostingDataResponseModel;
  vehicleBrakesOption: VehicleOption[];
  vehicleBrakesDisplayOption: VehicleOption[] = [];
  vehicleAbsOption: VehicleOption[];
  vehicleAbsDisplayOption: VehicleOption[] = [];
  vehicleInductionOption: VehicleOption[];
  vehicleInductionDisplayOption: VehicleOption[] = [];
  vehicleTransmissionOption: VehicleOption[];
  vehicleTransmissionDisplayOption: VehicleOption[] = [];
  vehicleRearAxleRatioOption: ExtraOption[];
  otherOptions: VehicleOption[];
  additionalOptions: VehicleOption[];
  originalOptions: VehicleOption[];
  showAllOptions = false;
  originalVehicleBrakes: VehicleOption[];
  originalVehiclelAbs: VehicleOption[];
  originalVehicleInduction: VehicleOption[];
  originalVehiclelTransmission: VehicleOption[];
  @Input() postingTypeString: string;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store,
    private readonly inventoryService: InventoryService,
    private readonly optionsAndAccessoriesFormsService: OptionsAndAccessoriesFormsService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = _cloneDeep(postingData);
      });
    this.originalPostingData = _cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));

    this.mechanicalForm = this.formBuilder.group({
      brakes: ['default'],
      abs: ['default'],
      induction: ['default'],
      transmission: ['default'],
      rearAxleRatio: ['default']
    });

    this.retrieveAllOptions();
    this.vehicleRearAxleRatioOption = this.postingData.inventoryItem.vehicleOptions.extraOptions.axleRatioOptions;

    // Store our original options so that they're always an available choice, regardless of if they come from the additional list
    this.originalOptions = _cloneDeep(
      this.store.selectSnapshot(EditPostingState.originalPosting)
    ).inventoryItem.vehicleOptions.mechanicalOptions.otherOptions;

    // include steering options
    const steeringOptions = this.originalPostingData.inventoryItem.vehicleOptions.mechanicalOptions.steeringOptions;
    if (steeringOptions?.length > 0 && this.originalOptions) {
      this.originalOptions = this.originalOptions.concat(steeringOptions);
    }

    // Set up our initial options, for when Show All Options isn't checked
    const options = [
      this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.otherOptions,
      this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.steeringOptions
    ];
    this.otherOptions = this.optionsAndAccessoriesFormsService.getInitialOptions(options, this.originalOptions);
    const mechanicalOptions = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions;

    const selectedBrake = mechanicalOptions.brakes?.find(brake => brake.installed);
    const selectedAb = mechanicalOptions.absOptions?.find(ab => ab.installed);
    const selectedInduction = mechanicalOptions.inductionOptions?.find(induction => induction.installed);
    const selectedTransmission = mechanicalOptions.transmissionOptions?.find(transmission => transmission.installed);

    this.inventoryService.getVehicleGeneralOptions(GeneralOptionsGroupName.MECHANICAL).subscribe(x => {
      // Pull down Additional Options and select the Show All Options checkbox if we have any installed options from that list
      this.additionalOptions = x;
      if (
        this.otherOptions.filter(o => o.fromAllOptionsList).length > 0 ||
        selectedBrake?.fromAllOptionsList ||
        selectedAb?.fromAllOptionsList ||
        selectedInduction?.fromAllOptionsList ||
        selectedTransmission.fromAllOptionsList
      ) {
        this.toggleAllOptions();
      } else {
        this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
          selectedBrake,
          SubGroupResponseName.BRAKES,
          this.originalVehicleBrakes,
          mechanicalOptions
        );
        this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
          selectedAb,
          SubGroupResponseName.ABS,
          this.originalVehiclelAbs,
          mechanicalOptions
        );
        this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
          selectedInduction,
          SubGroupResponseName.INDUCTION,
          this.originalVehicleInduction,
          mechanicalOptions
        );
        this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
          selectedTransmission,
          SubGroupResponseName.TRANSMISSION,
          this.originalVehiclelTransmission,
          mechanicalOptions
        );
        this.resetAllDropdownsToDefault();
      }
    });
    this.retrieveAllOptions();
    this.originalVehicleBrakes = this.originalPostingData.inventoryItem.vehicleOptions.mechanicalOptions.brakes;
    this.originalVehiclelAbs = this.originalPostingData.inventoryItem.vehicleOptions.mechanicalOptions.absOptions;
    this.originalVehicleInduction = this.originalPostingData.inventoryItem.vehicleOptions.mechanicalOptions.inductionOptions;
    this.originalVehiclelTransmission = this.originalPostingData.inventoryItem.vehicleOptions.mechanicalOptions.transmissionOptions;
    this.setAllOptionsToDefault();
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.OPTIONS_MECHANICAL
    );
  }

  setDefaultBrakesOption() {
    const installedBrakes = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.brakes?.find(
      b => b.installed
    );
    const displayBrakes = this.vehicleBrakesDisplayOption.find(
      o => o.optionCode === installedBrakes?.optionCode && o.afterMarket === installedBrakes?.afterMarket
    );
    this.mechanicalForm.patchValue({
      brakes: displayBrakes ? displayBrakes : 'default'
    });
  }

  setDefaultAbsOption() {
    const installedAbs = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.absOptions?.find(
      a => a.installed
    );
    const displayAbs = this.vehicleAbsDisplayOption.find(
      o => o.optionCode === installedAbs?.optionCode && o.afterMarket === installedAbs?.afterMarket
    );
    this.mechanicalForm.patchValue({
      abs: displayAbs ? displayAbs : 'default'
    });
  }

  setDefaultInductionOption() {
    const installedInduction = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.inductionOptions?.find(
      i => i.installed
    );
    const displayInduction = this.vehicleInductionDisplayOption.find(
      o => o.optionCode === installedInduction?.optionCode && o.afterMarket === installedInduction?.afterMarket
    );
    this.mechanicalForm.patchValue({
      induction: displayInduction ? displayInduction : 'default'
    });
  }

  setDefaultTransmissionOption() {
    const installedTransmission = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.transmissionOptions?.find(
      t => t.installed
    );
    const displayTransmission = this.vehicleTransmissionDisplayOption.find(
      o => o.optionCode === installedTransmission?.optionCode && o.afterMarket === installedTransmission?.afterMarket
    );
    this.mechanicalForm.patchValue({
      transmission: displayTransmission ? displayTransmission : 'default'
    });
  }

  setDefaultRearAxleRatioOption() {
    const installedRearAxleRatio = this.postingData.inventoryItem.vehicleOptions.extraOptions.axleRatioOptions.find(
      o => o.optionInstalled
    );
    const installedRearAxleRatioOptionsCode = installedRearAxleRatio ? installedRearAxleRatio.optionSeqId : 'default';
    this.mechanicalForm.patchValue({
      rearAxleRatio: installedRearAxleRatioOptionsCode
    });
  }

  public trackFormChanges() {
    // Brakes
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.mechanicalForm,
      'brakes',
      this.vehicleBrakesOption,
      this.vehicleBrakesDisplayOption
    );

    // ABS
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.mechanicalForm,
      'abs',
      this.vehicleAbsOption,
      this.vehicleAbsDisplayOption
    );

    // Induction
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.mechanicalForm,
      'induction',
      this.vehicleInductionOption,
      this.vehicleInductionDisplayOption
    );

    // Transmission
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.mechanicalForm,
      'transmission',
      this.vehicleTransmissionOption,
      this.vehicleTransmissionDisplayOption
    );

    // Rear Axle Ratio
    _set(_find(this.vehicleRearAxleRatioOption, { optionInstalled: true }), 'optionInstalled', false);
    _set(
      _find(this.vehicleRearAxleRatioOption, { optionSeqId: this.mechanicalForm.get('rearAxleRatio').value }),
      'optionInstalled',
      true
    );

    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  // Handles showing/hiding additional options
  toggleAllOptions() {
    this.showAllOptions = !this.showAllOptions;
    //These groups are selected in the top dropdowns
    const invalidMechanicalFormSubGroups = [
      SubGroupName.BRAKES,
      SubGroupName.ABS,
      SubGroupName.INDUCTION,
      SubGroupName.TRANSMISSION
    ];
    this.otherOptions = this.otherOptions.filter(emptyItem => emptyItem);
    this.resetAllDropdownOptions();

    if (this.showAllOptions) {
      // Show list of additional options and clean up any duplicates
      this.otherOptions = this.otherOptions.concat(
        this.additionalOptions.filter(x => !invalidMechanicalFormSubGroups.includes(x.subGroupName as SubGroupName))
      );
      this.otherOptions = this.optionsAndAccessoriesFormsService.cleanOptionsList(this.otherOptions);
      this.setAllExpandedDropdownList();
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.retrieveAllOptions();
      this.setAllOptionsToDefault();
    } else {
      this.otherOptions = this.otherOptions.filter(o => o.installed || o.fromAllOptionsList === false);
      this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.otherOptions = this.otherOptions;
      this.setAllBaseDropdownList();
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.setInitialOptions();
      this.resetAllDropdownsToDefault();
      this.filterAllVehicleDisplayOptions();
    }
  }

  setInitialOptions() {
    const mechanicalOptions = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions;
    const options: VehicleOption[][] = [mechanicalOptions.otherOptions];
    // Add the originally selected options as uninstalled so that they're always available, then clean the list to remove any duplicates
    this.otherOptions = this.optionsAndAccessoriesFormsService.getInitialOptions(options, this.originalOptions);
  }

  // Remove Steering options from other Options, if lists are empty use Orginal Options
  separateOtherAndSteeringOptions(options: VehicleOption[]): any {
    const powerSteeringSubGroupCode = 20;
    let steeringOptions = options.filter(option => option.subGroupCode === powerSteeringSubGroupCode);
    let otheroptions = options.filter(option => option.subGroupCode !== powerSteeringSubGroupCode);
    // results cannot be empty
    if (!steeringOptions.length) {
      steeringOptions = this.originalOptions.filter(option => option.subGroupCode === powerSteeringSubGroupCode);
    }
    if (!otheroptions.length) {
      otheroptions = this.originalOptions.filter(option => option.subGroupCode !== powerSteeringSubGroupCode);
    }
    return [otheroptions, steeringOptions];
  }

  // Updates the vehicle's exterior otherOptions array and persists the update to the store
  installOptions(options: VehicleOption[]) {
    const result = this.separateOtherAndSteeringOptions(options);
    const other = result[0];
    const steering = result[1];
    this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.otherOptions = other;
    this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.steeringOptions = steering;
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  removeOption(option: VehicleOption) {
    if (!this.showAllOptions && option.fromAllOptionsList) {
      this.otherOptions = this.otherOptions.filter(other => other.optionCode !== option.optionCode);
    }
  }

  resetAllDropdownsToDefault() {
    this.retrieveAllOptions();
    this.optionsAndAccessoriesFormsService.configureDropdownList(
      this.vehicleBrakesOption,
      this.vehicleBrakesDisplayOption
    );
    this.optionsAndAccessoriesFormsService.configureDropdownList(this.vehicleAbsOption, this.vehicleAbsDisplayOption);
    this.optionsAndAccessoriesFormsService.configureDropdownList(
      this.vehicleInductionOption,
      this.vehicleInductionDisplayOption
    );
    this.optionsAndAccessoriesFormsService.configureDropdownList(
      this.vehicleTransmissionOption,
      this.vehicleTransmissionDisplayOption
    );
    this.setAllOptionsToDefault();
  }

  private resetAllDropdownOptions() {
    this.vehicleBrakesOption = [];
    this.vehicleAbsOption = [];
    this.vehicleInductionOption = [];
    this.vehicleTransmissionOption = [];
    this.vehicleBrakesDisplayOption = [];
    this.vehicleAbsDisplayOption = [];
    this.vehicleInductionDisplayOption = [];
    this.vehicleTransmissionDisplayOption = [];
  }

  private retrieveAllOptions() {
    this.vehicleBrakesOption = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.brakes;
    this.vehicleAbsOption = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.absOptions;
    this.vehicleInductionOption = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.inductionOptions;
    this.vehicleTransmissionOption = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions.transmissionOptions;
  }

  private setAllOptionsToDefault() {
    this.setDefaultBrakesOption();
    this.setDefaultAbsOption();
    this.setDefaultInductionOption();
    this.setDefaultTransmissionOption();
    this.setDefaultRearAxleRatioOption();
  }

  setAllExpandedDropdownList() {
    const mechanicalOptions = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions;
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.BRAKES,
      SubGroupResponseName.BRAKES,
      this.vehicleBrakesOption,
      this.vehicleBrakesDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.ABS,
      SubGroupResponseName.ABS,
      this.vehicleAbsOption,
      this.vehicleAbsDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.INDUCTION,
      SubGroupResponseName.INDUCTION,
      this.vehicleInductionOption,
      this.vehicleInductionDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.TRANSMISSION,
      SubGroupResponseName.TRANSMISSION,
      this.vehicleTransmissionOption,
      this.vehicleTransmissionDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
  }

  setAllBaseDropdownList() {
    const mechanicalOptions = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions;
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.BRAKES,
      this.originalVehicleBrakes,
      mechanicalOptions
    );
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.ABS,
      this.originalVehiclelAbs,
      mechanicalOptions
    );
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.INDUCTION,
      this.originalVehicleInduction,
      mechanicalOptions
    );
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.TRANSMISSION,
      this.originalVehiclelTransmission,
      mechanicalOptions
    );
  }

  filterAllVehicleDisplayOptions() {
    const mechanicalOptions = this.postingData.inventoryItem.vehicleOptions.mechanicalOptions;
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.BRAKES,
      SubGroupResponseName.BRAKES,
      this.originalVehicleBrakes,
      this.vehicleBrakesDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.ABS,
      SubGroupResponseName.ABS,
      this.originalVehiclelAbs,
      this.vehicleAbsDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.INDUCTION,
      SubGroupResponseName.INDUCTION,
      this.originalVehicleInduction,
      this.vehicleInductionDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.TRANSMISSION,
      SubGroupResponseName.TRANSMISSION,
      this.originalVehiclelTransmission,
      this.vehicleTransmissionDisplayOption,
      mechanicalOptions,
      this.additionalOptions
    );
  }
}
